import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import {
  BrandRouterLinkDirective,
  CampaignEntryStatusComponent,
  EmptyStateComponent,
  InformationBoxComponent,
  IPatientMarketingCampaignsDialogRequest,
  PatientMarketingCampaignsDialogComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  BrandLinks,
  MarketingCampaignEntry,
  Patient,
} from '@principle-theorem/principle-core';
import {
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  HISTORY_DATE_TIME_FORMAT,
  multiFilter,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-patient-marketing-campaigns',
  imports: [
    CommonModule,
    NgCommonModule,
    RouterModule,
    InformationBoxComponent,
    BrandRouterLinkDirective,
    CampaignEntryStatusComponent,
    EmptyStateComponent,
  ],
  templateUrl: './patient-marketing-campaigns.component.html',
  styleUrl: './patient-marketing-campaigns.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientMarketingCampaignsComponent {
  private _dialog = inject(BasicDialogService);
  readonly dateFormat = HISTORY_DATE_TIME_FORMAT;
  patient = input.required<WithRef<IPatient>>();
  activeCampaignEntries: Signal<WithRef<IMarketingCampaignEntry>[]>;
  brandLinks = BrandLinks;

  constructor() {
    const activeCampaignEntries$ = toObservable(this.patient).pipe(
      switchMap((patient) => Patient.getCampaignEntries$(patient.ref)),
      multiFilter((entry) => MarketingCampaignEntry.isActive(entry))
    );
    this.activeCampaignEntries = toSignal(activeCampaignEntries$, {
      initialValue: [],
    });
  }

  async openDialog(): Promise<void> {
    const patient = this.patient();
    await this._dialog
      .open<
        PatientMarketingCampaignsDialogComponent,
        IPatientMarketingCampaignsDialogRequest,
        void
      >(
        PatientMarketingCampaignsDialogComponent,
        DialogPresets.large({ data: { patient } })
      )
      .afterClosed()
      .toPromise();
  }

  getCampaign$(
    entry: WithRef<IMarketingCampaignEntry>
  ): Observable<WithRef<IMarketingCampaign>> {
    return Firestore.doc$(entry.campaignRef);
  }
}
